import { languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { WarningTriangleIcon } from '@/utils/helpers/icons.helper';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledTabStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: var(--default-padding);
  row-gap: var(--small-padding);
`;

const StyledContentLanguageTab = styled.div<{ $active: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: var(--semi-xsmall-padding);
  padding: var(--content-tab-padding) 0;
  border-bottom: var(--active-tab-border-width) solid
    ${({ $active }) => ($active ? 'var(--red-main)' : 'transparent')};
  font-family: var(--font-family);
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;

  &.has-error {
    color: var(--red-main);
  }
`;

const StyledTabWarning = styled.span`
  display: inline-flex;
  align-items: center;
`;

const languageLabelKeys: Partial<Record<LanguagesEnum, string>> = {
  [LanguagesEnum.EN]: 'common.english',
  [LanguagesEnum.DK]: 'common.danish',
  [LanguagesEnum.DE]: 'common.german',
};

const WARNING_ICON_SIZE = 17;

export const useContentLanguageLabel = (): ((
  language: LanguagesEnum,
) => string) => {
  const { t } = useTranslation();

  return (language) => {
    const key = languageLabelKeys[language];

    return key ? t(key) : languageDisplayCode[language];
  };
};

export type ContentLanguageTabsProps = {
  languages: LanguagesEnum[];
  active: LanguagesEnum;
  onChange: (language: LanguagesEnum) => void;
  isEmpty: (language: LanguagesEnum) => boolean;
  hasError?: (language: LanguagesEnum) => boolean;
};

export const ContentLanguageTabs: React.FC<ContentLanguageTabsProps> = ({
  languages,
  active,
  onChange,
  isEmpty,
  hasError,
}) => {
  const { t } = useTranslation();
  const languageLabel = useContentLanguageLabel();
  const missingTranslationLabel = t(
    'globalSettings.languageTab.missingTranslation',
  );

  return (
    <StyledTabStrip role="tablist">
      {languages?.map((language) => {
        const isActive = language === active;

        return (
          <StyledContentLanguageTab
            key={language}
            $active={isActive}
            role="tab"
            tabIndex={0}
            aria-selected={isActive}
            className={`${isActive ? 'active' : ''} ${
              hasError?.(language) ? 'has-error' : ''
            }`}
            onClick={() => onChange(language)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onChange(language);
              }
            }}
          >
            {languageLabel(language)}
            {isEmpty(language) && (
              <StyledTabWarning
                role="img"
                aria-label={missingTranslationLabel}
                title={missingTranslationLabel}
              >
                <WarningTriangleIcon
                  color="var(--red-main)"
                  size={WARNING_ICON_SIZE}
                />
              </StyledTabWarning>
            )}
          </StyledContentLanguageTab>
        );
      })}
    </StyledTabStrip>
  );
};
